import { LoadingSpinner } from "@/components/loading-spinner";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { api } from "@/lib/api";
import {
  CreateTourAccommodation,
  createTourAccommodationToDto,
  createTourDayToDto,
  CreateTourService,
  createTourServiceToDto,
  TourAccommodation,
  TourAccommodationDtoSchema,
  tourAccommodationFromDto,
  tourAccommodationToDto,
  TourDay,
  TourDayDtoSchema,
  tourDayFromDto,
  tourDayToDto,
  TourService,
  TourServiceDtoSchema,
  tourServiceFromDto,
  tourServiceToDto,
} from "@/models";
import { LazyOperationEntity } from "@/models/commons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Delete, Save } from "lucide-react";
import { ReactNode, useState } from "react";
import { v4 as uuid } from "uuid";
import { Day, TourDayContextProvider } from "./context";
import { getTourDaysQueryOptions } from "@/hooks/tour-days";
import { getTourServicesQueryOptions } from "@/hooks/tour-services";
import { getTourAccommodationsQueryOptions } from "@/hooks/tour-accommodations";

function deepCloneArray<T extends object>(arr: T[]): T[] {
  return arr.map((item) => ({ ...item }));
}

function nooptourDay(
  tourId: string,
  day: number,
): LazyOperationEntity<TourDay> {
  return {
    operation: "noop",
    value: {
      day: day,
      id: uuid(),
      tourId: Number.parseInt(tourId),
      name: "",
    },
  };
}
function generateInitialStateOfDays(
  tourId: string,
  tourDays: TourDay[],
  services: TourService[],
  accommodations: TourAccommodation[],
): Day[] {
  const days: Day[] = [];

  for (const tourService of deepCloneArray(services)) {
    const day = days[tourService.day - 1];
    const serviceLazy = {
      operation: null,
      value: tourService,
    };
    if (day) {
      day.services.push(serviceLazy);
    } else {
      days[tourService.day - 1] = {
        services: [serviceLazy],
        tourDay: nooptourDay(tourId, tourService.day),
        accommodations: [],
      };
    }
  }

  for (const td of deepCloneArray(tourDays)) {
    const day = days[td.day - 1];
    if (day) {
      day.tourDay = {
        operation: null,
        value: td,
      };
    } else
      days[td.day - 1] = {
        services: [],
        accommodations: [],
        tourDay: {
          operation: null,
          value: td,
        },
      };
  }

  for (const acc of deepCloneArray(accommodations)) {
    const day = days[acc.day - 1];
    const lazyAcc = {
      operation: null,
      value: acc,
    };
    if (day) {
      day.accommodations.push(lazyAcc);
    } else {
      days[acc.day - 1] = {
        services: [],
        accommodations: [lazyAcc],
        tourDay: nooptourDay(tourId, acc.day),
      };
    }
  }

  for (let i = 0; i < days.length; i++) {
    if (!days[i]) {
      days[i] = {
        services: [],
        accommodations: [],
        tourDay: nooptourDay(tourId, i + 1),
      };
    }

    days[i].accommodations = days[i].accommodations.sort(
      (a, b) => a.value.priority - b.value.priority,
    );

    days[i].services = days[i].services.sort(
      (a, b) => (a.value.order ?? 0) - (b.value.order ?? 0),
    );
  }

  for (const day of days) {
    day.services.forEach((s, i) => {
      if (s.value.order === i + 1) return;
      s.value.order = i + 1;
      s.operation = "update";
    });
    day.accommodations.forEach((a, i) => {
      if (a.value.priority === i + 1) return;
      a.value.priority = i + 1;
      a.operation = "update";
    });
  }

  return days;
}

const tourDaysOperationsApiMap = {
  create: (td: TourDay) =>
    api
      .post("/api/tourdays", createTourDayToDto(td), TourDayDtoSchema)
      .then(tourDayFromDto),
  update: (td: TourDay) =>
    api
      .put("/api/tourdays", tourDayToDto(td), TourDayDtoSchema)
      .then(tourDayFromDto),
  delete: (td: TourDay) =>
    api.delete<void>(`/api/tourdays/$id`, undefined, {
      pathReplace: { $id: td.id },
    }),
} as const;

const tourServicesOperationApiMap = {
  create: (ts: TourService) =>
    api
      .post(
        "/api/tour-services",
        createTourServiceToDto(ts),
        TourServiceDtoSchema,
      )
      .then(tourServiceFromDto),
  update: (ts: TourService) =>
    api
      .put("/api/tour-services", tourServiceToDto(ts), TourServiceDtoSchema)
      .then(tourServiceFromDto),
  delete: (ts: TourService) =>
    api.delete<void>("/api/tour-services/$id", undefined, {
      pathReplace: { $id: ts.id },
    }),
} as const;

const tourAccommodationOperationApiMap = {
  create: (ta: TourAccommodation) =>
    api
      .post(
        "/api/tour-accommodations",
        createTourAccommodationToDto(ta),
        TourAccommodationDtoSchema,
      )
      .then(tourAccommodationFromDto),
  update: (ta: TourAccommodation) =>
    api
      .put(
        "/api/tour-accommodations",
        tourAccommodationToDto(ta),
        TourAccommodationDtoSchema,
      )
      .then(tourAccommodationFromDto),
  delete: (ta: TourAccommodation) =>
    api.delete<void>("/api/tour-accommodations/$id", undefined, {
      pathReplace: { $id: ta.id },
    }),
} as const;

export function TourDaysProvider({
  children,
  tourDays,
  services,
  tourId,
  accommodations,
}: {
  tourId: string;
  children?: ReactNode;
  tourDays: TourDay[];
  services: TourService[];
  accommodations: TourAccommodation[];
}) {
  const [dirty, setDirty] = useState(false);
  const [toDelete, setToDelete] = useState<Day[]>([]);
  const [days, setDaysFn] = useState<Day[]>(
    generateInitialStateOfDays(tourId, tourDays, services, accommodations),
  );
  const setDays = (newDays: Day[]) => {
    setDaysFn(newDays);
    setDirty(true);
  };

  function handleAddDay() {
    const netDay: Day = {
      tourDay: {
        operation: "create",
        value: {
          day: days.length + 1,
          id: uuid(),
          tourId: Number.parseInt(tourId),
          name: "",
        },
      },
      services: [],
      accommodations: [],
    };
    setDays([...days, netDay]);
  }

  function handleDeleteDay(day: number) {
    let newDays = [...days];
    const d = newDays[day - 1];

    switch (d.tourDay.operation) {
      case "create":
        d.tourDay.operation = "noop";
        break;
      case "update":
      case null:
        d.tourDay.operation = "delete";
        break;
    }

    d.services.forEach((s) => {
      switch (s.operation) {
        case "create":
          s.operation = "noop";
          break;
        case "update":
        case null:
          s.operation = "delete";
      }
    });

    d.accommodations.forEach((a) => {
      switch (a.operation) {
        case "create":
          a.operation = "noop";
          break;
        case "update":
        case null:
          a.operation = "delete";
      }
    });

    newDays = newDays.filter((d) => d.tourDay.value.day !== day);

    for (let i = d.tourDay.value.day - 1; i < newDays.length; i++) {
      const dy = newDays[i];
      dy.tourDay.value.day = i + 1;
      if (dy.tourDay.operation === null) dy.tourDay.operation = "update";

      dy.services.forEach((s) => {
        s.value.day = i + 1;
        if (s.operation === null) s.operation = "update";
      });

      dy.accommodations.forEach((a) => {
        a.value.day = i + 1;
        if (a.operation === null) a.operation = "update";
      });
    }

    setToDelete([...toDelete, d]);

    setDays(newDays);
  }

  function handleReorderOfDays(newOrder: number[]) {
    const newDays: Day[] = [];
    for (const dayNumber of newOrder) {
      const day = days[dayNumber - 1]!;
      day.tourDay.value.day = newDays.length + 1;
      if (day.tourDay.operation === null) day.tourDay.operation = "update";
      day.services.forEach((s) => {
        s.value.day = day.tourDay.value.day;
        if (s.operation === null) s.operation = "update";
      });
      day.accommodations.forEach((a) => {
        a.value.day = day.tourDay.value.day;
        if (a.operation === null) a.operation = "update";
      });
      newDays.push(day);
    }
    setDays(newDays);
  }

  function handleDayNameChange(day: number, name: string) {
    const newDays = [...days];
    const d = newDays[day - 1];
    d.tourDay.value.name = name;
    switch (d.tourDay.operation) {
      case null:
        d.tourDay.operation = "update";
        break;
      case "noop":
        d.tourDay.operation = "create";
        break;
    }
    setDays(newDays);
  }

  function handleAddService(day: number, tourService: CreateTourService) {
    const newDays = [...days];
    const d = newDays[day - 1];
    d.services.push({
      operation: "create",
      value: {
        id: uuid(),
        ...tourService,
      },
    });
    setDays(newDays);
  }

  function handleUpdateService(day: number, tourService: TourService) {
    const newDays = [...days];
    const s = newDays[day - 1]?.services.find(
      (s) => s.value.id === tourService.id,
    );
    if (!s) return;
    s.value = tourService;
    if (s.operation === null) s.operation = "update";
    setDays([...newDays]);
  }

  function handleReorderOfServices(dayNumber: number, newOrder: number[]) {
    const newDays = [...days];
    const day = newDays[dayNumber - 1];

    if (!day || day.services.length !== newOrder.length) return;

    day.services = newOrder
      .map((num) => day.services[num - 1])
      .map((d, i) => {
        d.value.order = i + 1;
        if (d.operation === null) d.operation = "update";
        return d;
      });
    setDays(newDays);
  }

  function handleDeleteService(day: number, tourServiceId: string) {
    const newDays = [...days];
    const sid = newDays[day - 1]?.services.findIndex(
      (s) => s.value.id === tourServiceId,
    );
    if (sid < 0) return;
    const s = newDays[day - 1]?.services[sid];
    if (s.operation !== "create") s.operation = "delete";
    else s.operation = "noop";

    setToDelete([
      ...toDelete,
      {
        tourDay: nooptourDay(tourId, -1),
        accommodations: [],
        services: [s],
      },
    ]);
    newDays[day - 1].services = newDays[day - 1].services
      .filter((s) => s.value.id !== tourServiceId)
      .map((s, i) => {
        const mustUpdate = s.value.order !== i + 1;
        s.value.order = i + 1;
        if (s.operation === null && mustUpdate) s.operation = "update";
        return s;
      });
    setDays(newDays);
  }

  function handleNewAccommodation(
    day: number,
    accommodation: CreateTourAccommodation,
  ) {
    const newDays = [...days];
    const d = newDays[day - 1];
    d.accommodations.push({
      operation: "create",
      value: {
        id: uuid(),
        ...accommodation,
      },
    });
    setDays(newDays);
  }

  function handleReorderAccommodations(
    dayNumber: number,
    accommodationOrder: number[],
  ) {
    const newDays = [...days];
    const day = newDays[dayNumber - 1];

    if (!day || day.accommodations.length !== accommodationOrder.length) return;

    day.accommodations = accommodationOrder
      .map((num) => day.accommodations[num - 1])
      .map((d, i) => {
        const mustUpdate = d.value.priority !== i + 1;
        d.value.priority = i + 1;
        if (d.operation === null && mustUpdate) d.operation = "update";
        return d;
      });
    setDays(newDays);
  }

  function handleUpdateAccommodation(
    day: number,
    accommodation: TourAccommodation,
  ) {
    const newDays = [...days];
    const a = newDays[day - 1]?.accommodations.find(
      (a) => a.value.id === accommodation.id,
    );
    if (!a) return;
    a.value = accommodation;
    if (a.operation === null) a.operation = "update";
    setDays([...newDays]);
  }

  function handleDeleteAccommodation(day: number, accommodationId: string) {
    const newDays = [...days];
    const sid = newDays[day - 1]?.accommodations.findIndex(
      (s) => s.value.id === accommodationId,
    );
    if (sid < 0) return;
    const s = newDays[day - 1]?.accommodations[sid];
    if (s.operation !== "create") s.operation = "delete";
    else s.operation = "noop";
    setToDelete([
      ...toDelete,
      {
        tourDay: nooptourDay(tourId, -1),
        accommodations: [s],
        services: [],
      },
    ]);
    newDays[day - 1].accommodations = newDays[day - 1].accommodations
      .filter((s) => s.value.id !== accommodationId)
      .map((a, i) => {
        const mustUpdate = s.value.priority !== i + 1;
        a.value.priority = i + 1;
        if (a.operation === null && mustUpdate) a.operation = "update";
        return a;
      });
    setDays(newDays);
  }

  function discard() {
    setDaysFn(
      generateInitialStateOfDays(tourId, tourDays, services, accommodations),
    );
    setDirty(false);
  }

  async function saveEntityOrDiscard<T>(
    entity: LazyOperationEntity<T>,
    options: {
      create: (entity: T) => Promise<T>;
      update: (entity: T) => Promise<T>;
      delete: (entity: T) => Promise<void>;
    },
  ) {
    switch (entity.operation) {
      case "create":
        return options.create(entity.value);
      case "update":
        return options.update(entity.value);
      case "delete":
        return options.delete(entity.value);
      case "noop":
        return Promise.resolve(null);
      case null:
        return Promise.resolve(entity.value);
    }
  }

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { mutate: save, isPending } = useMutation({
    mutationFn: async () => {
      const daysToOperate = [...days, ...toDelete];
      const allTourDays = daysToOperate
        .map((d) => d.tourDay)
        .map((td) => saveEntityOrDiscard(td, tourDaysOperationsApiMap));
      const allServices = daysToOperate
        .flatMap((d) => d.services)
        .map((ts) => saveEntityOrDiscard(ts, tourServicesOperationApiMap));
      const allAccommodations = daysToOperate
        .flatMap((d) => d.accommodations)
        .map((ta) => saveEntityOrDiscard(ta, tourAccommodationOperationApiMap));

      return Promise.all([
        Promise.all(allTourDays),
        Promise.all(allServices),
        Promise.all(allAccommodations),
      ]);
    },
    onSuccess: ([allTourDays, allServices, allAccommodations]) => {
      setDaysFn(
        generateInitialStateOfDays(
          tourId,
          allTourDays.filter(Boolean) as TourDay[],
          allServices.filter(Boolean) as TourService[],
          allAccommodations.filter(Boolean) as TourAccommodation[],
        ),
      );
      queryClient.invalidateQueries({
        queryKey: getTourDaysQueryOptions(tourId).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: getTourServicesQueryOptions(tourId).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: getTourAccommodationsQueryOptions(tourId).queryKey,
      });
      setDirty(false);

      toast({
        title: "toast.tourDaysUpdated",
        variant: "success",
      });
    },
    onError: () => {
      toast({
        title: "toast.updateTourDaysError",
        variant: "destructive",
      });
    },
  });

  return (
    <TourDayContextProvider.Provider
      value={{
        days,
        addNewDay: handleAddDay,
        deleteDay: handleDeleteDay,
        reorderDays: handleReorderOfDays,
        updateDayName: handleDayNameChange,
        reorderServices: handleReorderOfServices,
        addNewService: handleAddService,
        updateService: handleUpdateService,
        deleteService: handleDeleteService,
        addNewAccommodation: handleNewAccommodation,
        reorderAccommodations: handleReorderAccommodations,
        updateAccommodation: handleUpdateAccommodation,
        deleteAccommodation: handleDeleteAccommodation,
      }}
    >
      {children}
      {dirty ? (
        <div className="fixed bottom-8 right-8 z-10 flex items-center gap-6">
          <Button
            size="xxl"
            variant="destructive"
            onClick={() => {
              if (isPending) return;
              discard();
            }}
            disabled={isPending}
          >
            <Delete />
          </Button>
          <Button
            size="xxl"
            className="bg-green-600 text-white hover:bg-green-600/90"
            disabled={isPending}
            onClick={() => {
              if (isPending) return;
              save();
            }}
          >
            {isPending ? <LoadingSpinner /> : <Save />}
          </Button>
        </div>
      ) : (
        <></>
      )}
    </TourDayContextProvider.Provider>
  );
}
